/**
 * Сезонное испытание текущего месяца (ТЗ FR-GM-07).
 *
 * Считается по тому же журналу дней, что и задания: отдельного хранилища у
 * сезона нет, и хранить нечего — всё, что нужно, уже записано по дням.
 *
 * Прошлые сезоны не показываются: журнал держит месяц, и заводить ради
 * истории вторую запись — отдельное решение, а не побочный эффект этого.
 */

import type { DayActivity } from '../contracts/achievements.js';
import { CLEAN_ACCURACY } from '../contracts/achievements.js';
import type { SeasonChallenge, SeasonMonth, SeasonStatus } from '../contracts/seasons.js';
import { MONTHS_IN_YEAR, SEASON_CHALLENGES, SEASON_TARGETS } from '../contracts/seasons.js';
import type { Workspace } from '../contracts/workspace.js';

/**
 * Испытание месяца: три вида по кругу.
 *
 * Номер месяца — единственный источник: январь, апрель, июль и октябрь просят
 * дни практики, февраль, май, август и ноябрь — объём, остальные — чистые
 * занятия. Год на это не влияет, и человек, увидевший сентябрь один раз,
 * знает, каким будет следующий.
 */
/**
 * Номер месяца из календарной метки.
 *
 * Метка приходит строкой `YYYY-MM` и разбирается числом, а месяцев
 * двенадцать — по номеру экран берёт название. Значение вне диапазона
 * означало бы сломанную метку, и тогда берётся январь: сырой идентификатор
 * вместо слова хуже, чем неверный месяц в подписи сезона.
 */
function monthNumber(raw: string | undefined): SeasonMonth {
  const parsed = Number(raw);
  return parsed >= 1 && parsed <= MONTHS_IN_YEAR ? (parsed as SeasonMonth) : 1;
}

export function seasonChallenge(month: number): SeasonChallenge {
  const index = (month - 1) % SEASON_CHALLENGES.length;
  return SEASON_CHALLENGES[index]!;
}

/** Достигнутое за месяц значение испытания. */
function measure(days: readonly DayActivity[], challenge: SeasonChallenge): number {
  switch (challenge) {
    case 'practice-days':
      return days.filter((entry) => entry.sessions > 0).length;
    case 'characters':
      return days.reduce((total, entry) => total + entry.characters, 0);
    case 'clean-days':
      return days.filter((entry) => entry.bestAccuracy >= CLEAN_ACCURACY).length;
  }
}

/**
 * Состояние сезона на указанный день.
 *
 * В счёт идут только дни этого календарного месяца: сезон — месяц, а не
 * скользящее окно, иначе первое число ничем не отличалось бы от тридцатого.
 *
 * @param workspace пространство с журналом занятий;
 * @param todayIso момент, на который считается сезон.
 */
export function seasonProgress(workspace: Workspace, todayIso: string): SeasonStatus {
  const [year, month] = todayIso.slice(0, 7).split('-');
  const prefix = `${year}-${month}`;

  const challenge = seasonChallenge(Number(month));
  const target = SEASON_TARGETS[challenge];
  const current = measure(
    workspace.activityLog.filter((entry) => entry.date.startsWith(prefix)),
    challenge,
  );

  return {
    id: prefix,
    month: monthNumber(month),
    year: Number(year),
    challenge,
    target,
    current,
    done: current >= target,
  };
}
